import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { ArrowRightIcon, CheckIcon, CheckCircleIcon, SlidersHorizontalIcon, PrinterIcon, TagIcon, ChevronRightIcon, FileCheck2Icon, MessagesSquareIcon, PenLineIcon, PackageCheckIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { designGroups } from '../data/vormgeving';
import { buildBreadcrumbList, buildServiceSchema } from '../lib/structuredData';
const EASE = [0.23, 1, 0.32, 1];
const totalItems = designGroups.reduce((sum, group) => sum + group.items.length, 0);
const fadeUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-60px'
  },
  transition: {
    duration: 0.5,
    ease: EASE
  }
};
const figmaBenefits = [{
  title: 'Kleuren en teksten zelf aanpassen',
  body: 'Een adres gewijzigd of een actie erbij? Je past het aan in het document en exporteert opnieuw. Zonder wachten op een reactie.'
}, {
  title: 'Varianten staan al klaar',
  body: 'Van elk onderdeel leveren we varianten mee, bijvoorbeeld een liggende en staande versie of een lichte en donkere uitvoering.'
}, {
  title: 'Alles op één plek, ook over jaren',
  body: 'Je huisstijl ligt niet verspreid over losse bestanden op iemands laptop. Je hebt één document dat het complete verhaal bevat.'
}];
const werkwijze = [{
  title: 'Gesprek over je vak',
  body: 'We willen weten wat je doet, voor wie, en hoe je gezien wilt worden. Vaak levert dat meer op dan een lijst met voorkeuren.',
  icon: MessagesSquareIcon
}, {
  title: 'Richting kiezen',
  body: 'Je krijgt twee richtingen te zien, uitgewerkt op echte toepassingen. Geen losse logo op een witte pagina.',
  icon: PenLineIcon
}, {
  title: 'Uitwerken',
  body: 'De gekozen richting wordt doorgetrokken naar alle onderdelen die je nodig hebt, van visitekaartje tot e-mailfooter.',
  icon: FileCheck2Icon
}, {
  title: 'Opleveren in Figma',
  body: 'Je krijgt het Figma-document, de exportbestanden en een korte uitleg zodat je er zelf mee verder kunt.',
  icon: PackageCheckIcon
}];
export const VormgevingPage: React.FC = () => {
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-2819" data-mp="1fnwhhr62qj8t">
      <SEO title="Digitale vormgeving en huisstijl laten maken | Noord Digital" description="Huisstijl, logo, drukwerk en digitale sjablonen voor ondernemers in Noord-Holland Noord. Opgeleverd als Figma-document dat je zelf kunt aanpassen. Prijzen op aanvraag." keywords="digitale vormgeving, huisstijl laten maken, logo laten ontwerpen, drukwerk ontwerp, visitekaartjes laten maken, huisstijlhandboek, figma huisstijl, vormgeving Noord-Holland" canonical="https://noorddigital.nl/vormgeving" jsonLd={[buildServiceSchema({
      name: 'Digitale vormgeving',
      description: 'Logo en huisstijl, drukwerk en digitale sjablonen voor ondernemers in Noord-Holland Noord, opgeleverd als aanpasbaar Figma-document.',
      path: '/vormgeving',
      serviceType: 'Grafische vormgeving en huisstijlontwerp'
    }), buildBreadcrumbList([{
      name: 'Digitale vormgeving',
      path: '/vormgeving'
    }])]} data-id="element-2820" data-mp="23cp7z83xfyw1" />
      <Header data-id="element-2821" data-mp="3nv9f6iqwpm0s" />

      <main data-id="element-2822" data-mp="3nvsz2504akhg">
        {/* Hero, in dezelfde huisstijl als de andere dienstpagina's */}
        <section className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] py-16 md:py-20" data-id="element-2823" data-mp="5pwemux4mana">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2824" data-mp="1n9vdieity1p">
            <nav aria-label="Kruimelpad" className="mb-8" data-id="element-2825" data-mp="1h4jolc9t0d3q">
              <ol className="flex items-center gap-1.5 text-sm text-white text-opacity-70" data-id="element-2826" data-mp="1h2yxxxqm1eb5">
                <li data-id="element-2827" data-mp="1d2exolaqpuks">
                  <a href="/" className="hover:text-white transition-colors duration-200" data-id="element-2828" data-mp="1d5142hlwa41f">
                    Home
                  </a>
                </li>
                <li aria-hidden="true" data-id="element-2829" data-mp="2hdt4cgp6bd43">
                  <ChevronRightIcon className="h-3.5 w-3.5 text-white text-opacity-40" data-id="element-2830" data-mp="2hcrrq7sieik9" />
                </li>
                <li className="font-medium text-white" aria-current="page" data-id="element-2831" data-mp="2mdp8cfxvf4jn">
                  Digitale vormgeving
                </li>
              </ol>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center" data-id="element-2832" data-mp="2vd2i79pwpv4w">
              <motion.div className="lg:col-span-7" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              ease: EASE
            }} data-id="element-2833" data-mp="304klw5jbe9tk">
                <h1 className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl" data-id="element-2834" data-mp="13dye7jv4qrns">
                  Digitale vormgeving en huisstijl voor lokale ondernemers
                </h1>
                <p className="text-white text-opacity-90 mb-8 text-lg leading-relaxed" data-id="element-2835" data-mp="tfsd34h5dpjz">
                  Je werk is in orde, maar je uitstraling loopt achter. Dat merkt een klant voordat hij je gesproken heeft. Wij zetten je merk één keer goed neer en leveren alles zo op dat je er zelf mee vooruit kunt. Van logo en huisstijl tot visitekaartjes, gevelreclame en de sjablonen die je elke week gebruikt. Vormgeving kan los, ook zonder website bij ons.
                </p>
                <div className="flex flex-wrap gap-4" data-id="element-2836" data-mp="1cctjmtqcnxq1">
                  <a href="/#contact" className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-lg group" data-id="element-2837" data-mp="17es3agkb6ci9">
                    Vraag een prijsopgave aan
                    <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2838" data-mp="3ucckzsasr6d1" />
                  </a>
                  <a href="#figma" className="inline-flex items-center px-6 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg group" data-id="element-2839" data-mp="36b4v5bz5mkj">
                    Wat is de Figma-oplevering?
                    <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2840" data-mp="3bluw5yt34uwy" />
                  </a>
                </div>
              </motion.div>

              <motion.div className="lg:col-span-5" initial={{
              opacity: 0,
              scale: 0.96
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 0.1,
              ease: EASE
            }} data-id="element-2841" data-mp="3flc2svk8zy3p">
                <div className="bg-white rounded-2xl p-8 shadow-2xl" data-id="element-2842" data-mp="2pcwg5utpw008">
                  <div className="text-center mb-6" data-id="element-2843" data-mp="2pb37k9zjn5pl">
                    <div className="inline-flex items-center justify-center w-28 h-28 bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-full mb-4 p-3" data-id="element-2844" data-mp="2kfnx7s0qvrxx">
                      <img src="https://cdn.magicpatterns.com/uploads/hr1JC9wRjcR2KJD3mjAfSG/Norden_met_kleur_waaier.png" alt="Norden met kleurwaaier, digitale vormgeving" className="w-full h-full object-contain" data-id="element-2845" data-mp="2ucirfypzj66e" />
                    </div>
                    <h2 className="font-bold text-[#111827] text-2xl mb-2" data-id="element-2846" data-mp="3i121ojslht14">
                      {totalItems} onderdelen, één huisstijl
                    </h2>
                    <p className="text-[#6B7280] text-sm" data-id="element-2847" data-mp="1no2jciwtykq5">
                      Van logo tot gevelreclame, alles uit dezelfde bron
                    </p>
                  </div>
                  <div className="space-y-2.5 pt-5 border-t border-gray-200" data-id="element-2848" data-mp="1ir2cw6w2jfyi">
                    {designGroups.map(group => <div key={group.id} className="flex items-center justify-between gap-3" data-id="element-2849" data-mp="11umz7t3fp77t">
                        <span className="flex items-center gap-3" data-id="element-2850" data-mp="16npwab7f6ysr">
                          <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-2851" data-mp="1bjer618hvriq" />
                          <span className="text-sm text-[#111827] font-medium" data-id="element-2852" data-mp="dy98xrynq83e">
                            {group.title}
                          </span>
                        </span>
                        <span className="text-sm text-[#6B7280]" data-id="element-2853" data-mp="nuvqa6rryh0o">
                          {group.items.length}
                        </span>
                      </div>)}
                    <div className="flex items-center gap-3 pt-3 mt-1 border-t border-gray-100" data-id="element-2854" data-mp="1zb28psjv9q8c">
                      <SlidersHorizontalIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-2855" data-mp="23b4cq8d2qvqs" />
                      <span className="text-sm text-[#6B7280]" data-id="element-2856" data-mp="3nug5s23un28m">
                        Opgeleverd in Figma, zelf aan te passen
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Wat we maken */}
        <section className="w-full bg-[#F7F8FA] py-20 md:py-24" data-id="element-2857" data-mp="3a1qgnot0xh7a">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2858" data-mp="353yjaf9m12vp">
            <motion.div {...fadeUp} className="max-w-3xl mb-14" data-id="element-2859" data-mp="304lv9r705prj">
              <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-4" data-id="element-2860" data-mp="30310q1kou6to">
                Wat we voor je maken
              </h2>
              <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-2861" data-mp="2r3xh8whlzpen">
                Je hoeft niet alles in één keer af te nemen. De meeste
                ondernemers beginnen bij hun merk en vullen de rest aan zodra
                ze het nodig hebben.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch" data-id="element-2862" data-mp="2n5ufeeifhlth">
              {designGroups.map((group, index) => {
              const Icon = group.icon;
              return <motion.article key={group.id} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-60px'
              }} transition={{
                duration: 0.45,
                delay: index * 0.06,
                ease: EASE
              }} className="bg-white rounded-2xl border border-gray-200 p-8 flex flex-col" data-id="element-2863" data-mp="2w7ap0k1f5jwn" data-mpi={index}>
                    <div className="flex items-start justify-between gap-4 mb-5" data-id="element-2864" data-mp="1frhjzy7lzi3i">
                      <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#EEF5FA] text-[#004AC7] flex-shrink-0" data-id="element-2865" data-mp="3rfcldk8kkg46">
                        <Icon className="h-6 w-6" aria-hidden="true" data-id="element-2866" data-mp="2rnx2m651m8" />
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-wide text-[#6B7280] bg-[#F7F8FA] border border-gray-200 rounded-full px-3 py-1.5" data-id="element-2867" data-mp="3igr9g7xlrzcd">
                        {group.items.length} onderdelen
                      </span>
                    </div>
                    <h3 className="font-bold text-[#111827] text-2xl mb-3" data-id="element-2868" data-mp="j0cd6asui27d">
                      {group.title}
                    </h3>
                    <p className="text-[#6B7280] leading-relaxed mb-6" data-id="element-2869" data-mp="501pthqqv5qy">
                      {group.intro}
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5" data-id="element-2870" data-mp="2pqsddskzr1y7">
                      {group.items.map(item => <li key={item} className="flex items-start gap-2.5 text-[#111827]" data-id="element-2871" data-mp="2uqere48n8m82">
                          <CheckIcon className="h-4 w-4 text-[#004AC7] mt-1 flex-shrink-0" aria-hidden="true" data-id="element-2872" data-mp="26ldtigx2pdbt" />
                          <span className="text-sm leading-relaxed" data-id="element-2873" data-mp="1wwch10pqle2l">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                  </motion.article>;
            })}
            </div>
          </div>
        </section>

        {/* Figma, het onderscheidende punt */}
        <section id="figma" className="w-full bg-[#1F222C] py-20 md:py-28 scroll-mt-24" data-id="element-2874" data-mp="1iy5xkpljbgmg">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2875" data-mp="1dzb9p98pn1vh">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-id="element-2876" data-mp="3d9kr7hho207h">
              <motion.div {...fadeUp} className="lg:col-span-5" data-id="element-2877" data-mp="38bsmtnzu4lme">
                <p className="inline-flex items-center gap-2 text-[#7FB2FF] text-sm font-semibold mb-5" data-id="element-2878" data-mp="3fvk2z7smqgxm">
                  <SlidersHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-2879" data-mp="3ksto4b8zk6fn" />
                  Zo levert bijna niemand in de regio op
                </p>
                <h2 className="text-white font-bold text-3xl md:text-4xl lg:text-[44px] leading-tight mb-6" data-id="element-2880" data-mp="3pmxyz5ldgym0">
                  Je huisstijl komt als Figma-document, niet als platte pdf
                </h2>
                <p className="text-white/75 text-lg leading-relaxed mb-6" data-id="element-2881" data-mp="31uzlceygar5p">
                  Bij de meeste bureaus krijg je bestanden die je alleen kunt
                  bekijken. Voor elke wijziging ga je terug naar de ontwerper,
                  en dat kost tijd en geld.
                </p>
                <p className="text-white/75 leading-relaxed" data-id="element-2882" data-mp="m3v7w4oqjv31">
                  Wij leveren je huisstijl op als een werkend Figma-document.
                  Kleuren, teksten en varianten staan erin klaar en je kunt ze
                  zelf aanpassen. Je blijft eigenaar van je eigen merk, ook als
                  je ooit met iemand anders verder wilt.
                </p>
              </motion.div>

              <motion.div className="lg:col-span-7" initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.55,
              delay: 0.08,
              ease: EASE
            }} data-id="element-2883" data-mp="ymh2ke8gsssk">
                <img src="https://cdn.magicpatterns.com/patterns/generated-images/3dad8674-5068-4992-9539-32017ceda909.jpg" alt="Huisstijldocument met kleurstalen, typografie en logovarianten naast een instellingenpaneel" loading="lazy" className="w-full h-auto rounded-2xl border border-white/10 shadow-2xl" data-id="element-2884" data-mp="1cf6nefc9bro3" />
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14 items-stretch" data-id="element-2885" data-mp="29x2gbeaolp70">
              {figmaBenefits.map((benefit, index) => <motion.div key={benefit.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-40px'
            }} transition={{
              duration: 0.4,
              delay: index * 0.06,
              ease: EASE
            }} className="bg-white/5 border border-white/10 rounded-2xl p-7" data-id="element-2886" data-mp="20xp96sa15cpg" data-mpi={index}>
                  <h3 className="text-white font-semibold text-lg mb-3" data-id="element-2887" data-mp="39z0c7ldqugbt">
                    {benefit.title}
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed" data-id="element-2888" data-mp="357h5xnt1ii6w">
                    {benefit.body}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Werkwijze */}
        <section className="w-full bg-white py-20 md:py-24" data-id="element-2889" data-mp="2w60xhllscq5b">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2890" data-mp="3swtc0et2ma4z">
            <motion.h2 {...fadeUp} className="font-bold text-[#111827] text-3xl md:text-4xl mb-14 max-w-2xl" data-id="element-2891" data-mp="3oxklditbusdn">
              Hoe een vormgevingstraject bij ons loopt
            </motion.h2>

            <ol className="relative border-l border-gray-200 ml-5 md:ml-6" data-id="element-2892" data-mp="3ey3ita9dgulw">
              {werkwijze.map((step, index) => {
              const Icon = step.icon;
              return <motion.li key={step.title} initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-40px'
              }} transition={{
                duration: 0.4,
                delay: index * 0.06,
                ease: EASE
              }} className="relative pl-8 md:pl-12 pb-12 last:pb-0" data-id="element-2893" data-mp="23hln5cdlmw0b" data-mpi={index}>
                    <span className="absolute -left-5 md:-left-6 flex items-center justify-center w-10 md:w-12 h-10 md:h-12 rounded-full bg-[#004AC7] text-white" data-id="element-2894" data-mp="2m4gesecbxtow">
                      <Icon className="h-5 w-5" aria-hidden="true" data-id="element-2895" data-mp="rqfm6b7di2x9" />
                    </span>
                    <p className="text-sm font-semibold text-[#004AC7] mb-1" data-id="element-2896" data-mp="woh24n9i5m4i">
                      Stap {index + 1}
                    </p>
                    <h3 className="font-bold text-[#111827] text-xl mb-2" data-id="element-2897" data-mp="2ykyamkd0oil0">
                      {step.title}
                    </h3>
                    <p className="text-[#6B7280] leading-relaxed max-w-2xl" data-id="element-2898" data-mp="38ccf76jts8l0">
                      {step.body}
                    </p>
                  </motion.li>;
            })}
            </ol>
          </div>
        </section>

        {/* Drukwerk via partner */}
        <section className="w-full bg-[#F7F8FA] py-20 md:py-24" data-id="element-2899" data-mp="2fprfnit74q6d">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2900" data-mp="2apvju6unk51w">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2901" data-mp="2oolg5k9ne0p7">
              <motion.div {...fadeUp} data-id="element-2902" data-mp="2km8dj4q76iwn">
                <img src="https://cdn.magicpatterns.com/patterns/generated-images/3585c62b-515c-4078-b280-0d4bf30f3452.jpg" alt="Gedrukte flyers, poster, menukaart en een opgerolde banner op een lichte ondergrond" loading="lazy" className="w-full h-auto rounded-2xl shadow-lg" data-id="element-2903" data-mp="2kl6plzo321xi" />
              </motion.div>
              <motion.div {...fadeUp} data-id="element-2904" data-mp="cfwefi572a5i">
                <p className="inline-flex items-center gap-2 text-[#004AC7] text-sm font-semibold mb-4" data-id="element-2905" data-mp="ccs5y8kp4s91">
                  <PrinterIcon className="h-4 w-4" aria-hidden="true" data-id="element-2906" data-mp="2gyvqvug9auf" />
                  Drukwerk
                </p>
                <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-5" data-id="element-2907" data-mp="q5zwdwx1jhp5">
                  Het laten drukken kunnen we regelen
                </h2>
                <p className="text-[#4B5563] text-lg leading-relaxed mb-5" data-id="element-2908" data-mp="v7p7fck9jvd8">
                  Wij zijn geen drukkerij. Voor het drukwerk werken we samen met
                  een externe partner die we vertrouwen. Vraag je erom, dan
                  regelen we het en houd je één aanspreekpunt.
                </p>
                <p className="text-[#6B7280] leading-relaxed mb-6" data-id="element-2909" data-mp="1dw3yfzukz9e1">
                  Het zit niet standaard in de prijs. In de opdracht staat het
                  ontwerp, en het drukken komt er alleen bij als je dat wilt. De
                  kosten van de drukker rekenen we los door, zodat je ziet wat
                  je waarvoor betaalt.
                </p>
                <p className="text-[#111827] font-medium leading-relaxed" data-id="element-2910" data-mp="2vqvlthu88zqz">
                  Wil je zelf naar je eigen drukker? Ook goed. Je krijgt de
                  drukklare bestanden mee.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Kortingsregeling en prijzen */}
        <section className="w-full bg-white py-20 md:py-24" data-id="element-2911" data-mp="2csj1r11s25gm">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2912" data-mp="2cu3xuv3qpwgl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch" data-id="element-2913" data-mp="2hsp0102jyq7o">
              <motion.div {...fadeUp} className="bg-[#EEF5FA] rounded-2xl p-8 md:p-10 border border-[#D6E6F5]" data-id="element-2914" data-mp="b0whdice58t4">
                <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-white text-[#004AC7] mb-5" data-id="element-2915" data-mp="fxwnft98qbk8">
                  <TagIcon className="h-6 w-6" aria-hidden="true" data-id="element-2916" data-mp="1vjgu18i53o59" />
                </span>
                <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-4" data-id="element-2917" data-mp="2a84lb2kydbpe">
                  Korting bij een website en hosting bij ons
                </h2>
                <p className="text-[#4B5563] leading-relaxed mb-4" data-id="element-2918" data-mp="25g3bdnb9bx2j">
                  Klanten met een website en hosting bij ons krijgen korting op
                  vormgeving. De hoogte stemmen we per traject af, omdat die
                  afhangt van de omvang van het werk en van wat je al bij ons
                  hebt lopen.
                </p>
                <p className="text-[#6B7280] leading-relaxed" data-id="element-2919" data-mp="2o7cos7o5f5ze">
                  Dat noemen we liever eerlijk zo, dan dat we een percentage
                  roepen dat later toch niet blijkt te kloppen.
                </p>
              </motion.div>

              <motion.div {...fadeUp} className="bg-[#F7F8FA] rounded-2xl p-8 md:p-10 border border-gray-200 flex flex-col" data-id="element-2920" data-mp="tvpkzvna3v6v">
                <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-4" data-id="element-2921" data-mp="1nkdf3liaul4w">
                  Prijzen op aanvraag
                </h2>
                <p className="text-[#4B5563] leading-relaxed mb-4" data-id="element-2922" data-mp="1sl1gn145mkdd">
                  Een logo voor een praktijk van twee personen is een ander
                  traject dan een complete huisstijl met gevelreclame en
                  bedrijfskleding. Daarom noemen we geen vanaf-prijs die niets
                  zegt.
                </p>
                <p className="text-[#6B7280] leading-relaxed mb-8" data-id="element-2923" data-mp="2b8wok4d353yk">
                  Vertel wat je nodig hebt en je krijgt een opgave met een
                  vaste prijs per onderdeel. Vormgeving kan los worden
                  afgenomen, ook als je geen website bij ons hebt.
                </p>
                <a href="/#contact" className="inline-flex items-center justify-center bg-[#004AC7] hover:bg-[#003A9E] text-white font-semibold py-4 px-8 rounded-lg transition-colors duration-200 shadow-md group mt-auto self-start" data-id="element-2924" data-mp="2q5nnkciukdnn">
                  Vraag een prijsopgave aan
                  <ArrowRightIcon className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-200" data-id="element-2925" data-mp="4l6429gy32on" />
                </a>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Beer, de ontwerper */}
        <section className="w-full bg-[#F7F8FA] py-20 md:py-24" data-id="element-2926" data-mp="sa8hf54jq9dc">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2927" data-mp="njs5jpgdivt3">
            <motion.div {...fadeUp} className="bg-white rounded-2xl border border-gray-200 p-8 md:p-12 flex flex-col md:flex-row items-center gap-10" data-id="element-2928" data-mp="nhp3u85uze8j">
              <img src="https://cdn.magicpatterns.com/uploads/3h5jPU9M1S7oTcbxfUFYhr/image.png" alt="Beer, ontwerper bij Noord Digital" loading="lazy" className="w-40 h-40 md:w-48 md:h-48 rounded-full object-cover border-4 border-[#EEF5FA] flex-shrink-0" style={{
              objectPosition: 'center 35%'
            }} data-id="element-2929" data-mp="1g1e0ru9p3jse" />
              <div data-id="element-2930" data-mp="qs9zlsc8orbh">
                <p className="text-sm font-semibold text-[#004AC7] mb-2" data-id="element-2931" data-mp="qrqhaaiog0wx">
                  Wie het maakt
                </p>
                <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-4" data-id="element-2932" data-mp="1ej5iu12mqe74">
                  Beer is de ontwerper achter dit werk
                </h2>
                <p className="text-[#4B5563] leading-relaxed mb-6" data-id="element-2933" data-mp="14savetdnd8k3">
                  Beer maakt de vormgeving bij Noord Digital. Hij werkt liever
                  aan iets dat jaren meegaat dan aan een ontwerp dat er nu even
                  goed uitziet. Je hebt gedurende het traject met hem direct
                  contact, niet met een tussenpersoon.
                </p>
                <a href="/over-ons/beer" className="inline-flex items-center gap-2 text-[#004AC7] font-semibold hover:text-[#003A9E] transition-colors duration-200 group" data-id="element-2934" data-mp="2299qcjixuy3e">
                  Meer over Beer
                  <ArrowRightIcon className="h-4 w-4 transform group-hover:translate-x-1 transition-transform duration-200" aria-hidden="true" data-id="element-2935" data-mp="gy4wag5co5u" />
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Slot-CTA */}
        <section className="w-full bg-gradient-to-br from-[#007AFF] to-[#005FCC] py-16 md:py-20" data-id="element-2936" data-mp="2z1zm9h18z4q0">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2937" data-mp="3cxjthf7m5yhl">
            <div className="flex flex-col lg:flex-row lg:items-center gap-8" data-id="element-2938" data-mp="38whtgw5vhiek">
              <div className="lg:flex-grow max-w-2xl" data-id="element-2939" data-mp="s8e0gtzoll87">
                <h2 className="text-white font-bold text-3xl md:text-4xl mb-4" data-id="element-2940" data-mp="o7u2xpb74flj">
                  Laat je merk een keer goed vastleggen
                </h2>
                <p className="text-white text-opacity-90 text-lg leading-relaxed" data-id="element-2941" data-mp="3h1ix3hhx4wry">
                  Een gesprek van een half uur is genoeg om te bepalen wat je
                  nodig hebt en wat het ongeveer kost. We komen langs in de
                  regio of bellen, wat jou beter past.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0" data-id="element-2942" data-mp="3uzqz43ttao9z">
                <a href="/#contact" className="inline-flex items-center justify-center bg-white text-[#007AFF] font-semibold py-4 px-8 rounded-lg hover:bg-gray-100 transition-colors duration-200 shadow-md" data-id="element-2943" data-mp="2ydnn88pfg344">
                  Plan een kennismaking
                </a>
                <a href="/websites" className="inline-flex items-center justify-center border-2 border-white text-white font-semibold py-4 px-8 rounded-lg hover:bg-white/10 transition-colors duration-200" data-id="element-2944" data-mp="3c527sjyzgcda">
                  Bekijk websites
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-2945" data-mp="2727y97lrokpv" />
    </div>;
};